import {useEffect} from 'react';
import {create} from 'zustand';
import {SessionStorage} from '@chekin/core';
import {STORAGE_KEYS} from 'utils/constants';
import {getLockedFields, type IVLockableField} from '@chekin/shared/guest-form';

type IVLockState = {
  scanned: Record<string, unknown> | null;
  lockedFields: IVLockableField[];
  unlocked: boolean;
  setScan: (scanned: Record<string, unknown>) => void;
  unlock: () => void;
  clear: () => void;
};

const NO_FIELDS: IVLockableField[] = [];
const INITIAL_STATE = {scanned: null, lockedFields: [], unlocked: false};

export const useIVLockStore = create<IVLockState>((set, get) => ({
  ...INITIAL_STATE,
  setScan: scanned => {
    const isSameScan = JSON.stringify(get().scanned) === JSON.stringify(scanned);
    set({
      scanned,
      lockedFields: getLockedFields(scanned),
      unlocked: isSameScan ? get().unlocked : false,
    });
  },
  unlock: () => set({unlocked: true}),
  clear: () => set(INITIAL_STATE),
}));

export const selectEffectiveLockedFields = (state: IVLockState): IVLockableField[] =>
  state.unlocked ? NO_FIELDS : state.lockedFields;

const NEW_GUEST_SCAN_OWNER = 'new';
const toScanOwner = (guestId?: string | null) => guestId || NEW_GUEST_SCAN_OWNER;

export function saveIVScan(scanned: object, guestId?: string | null) {
  SessionStorage.set(STORAGE_KEYS.ivScannedData, scanned);
  SessionStorage.set(STORAGE_KEYS.ivScannedGuestId, toScanOwner(guestId));
}

export function getIVScanForGuest(guestId?: string | null) {
  const scanned = SessionStorage.get<Record<string, unknown>>(STORAGE_KEYS.ivScannedData);
  if (!scanned) return null;
  const owner = SessionStorage.get<string>(STORAGE_KEYS.ivScannedGuestId);
  return owner === toScanOwner(guestId) ? scanned : null;
}

export function removeIVScan() {
  SessionStorage.remove(STORAGE_KEYS.ivScannedData);
  SessionStorage.remove(STORAGE_KEYS.ivScannedGuestId);
}

/**
 * abandoned scan owned by "new" cannot prefill and lock the next new guest's form.
 * Never call it on the IV -> form navigation.
 */
export function discardIVScan() {
  removeIVScan();
  SessionStorage.remove(STORAGE_KEYS.personalFormData);
  useIVLockStore.getState().clear();
}

/**
 * submitting (back navigation does not mark the form dirty) must not leave the scan
 * behind, or the next guest's form would be locked with this guest's data.
 */
export function useDiscardIVScanOnLeave() {
  useEffect(
    () => () => {
      discardIVScan();
    },
    [],
  );
}
